import { ChangeEvent, FocusEvent, useState } from 'react';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import { WrappedFieldProps } from 'redux-form';

interface FailureProbabilityInputProps {
  value: number | string | null | undefined;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  id?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: string;
  isInvalid?: boolean;
  errorMessage?: string | null;
  [key: string]: any;
}

/**
 * Reusable Failure Probability Input Component
 * Displays as percentage (e.g., "55%") when not focused
 * Displays as decimal (e.g., "0.55") when focused for editing
 * Underlying value always remains in decimal format (0.00-0.99)
 */
export const FailureProbabilityInput = ({
  value,
  onChange,
  onBlur,
  onFocus,
  id,
  placeholder = '0%',
  min = 0,
  max = 0.99,
  step = '0.01',
  isInvalid = false,
  errorMessage = null,
  ...props
}: FailureProbabilityInputProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const getDisplayValue = (): string => {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (isFocused) {
      return String(value);
    } else {
      const percentValue = (parseFloat(String(value)) * 100).toFixed(0);
      return `${percentValue}%`;
    }
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        id={id}
        // Text while blurred so the value can read "55%"; number while editing so the
        // spinner, min/max and step apply to the underlying decimal.
        type={isFocused ? 'number' : 'text'}
        value={getDisplayValue()}
        placeholder={placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        error={isInvalid}
        slotProps={{
          htmlInput: { min, max, step, onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur() },
        }}
        {...props}
      />
      {errorMessage && (
        // Absolutely positioned so showing or clearing the message never moves the row.
        <FormHelperText
          error
          sx={{ position: 'absolute', top: '100%', left: 0, whiteSpace: 'nowrap', fontSize: '0.875rem', m: 0 }}
        >
          {errorMessage}
        </FormHelperText>
      )}
    </Box>
  );
};

/**
 * Redux Form wrapper for FailureProbabilityInput
 * Use this with redux-form's <Field> component
 */
export const renderFailureProbabilityInput = ({
  input,
  meta: { touched, error },
  ...props
}: WrappedFieldProps &
  Omit<FailureProbabilityInputProps, 'value' | 'onChange' | 'onBlur' | 'onFocus' | 'isInvalid' | 'errorMessage'>) => {
  return (
    <FailureProbabilityInput
      value={input.value}
      onChange={input.onChange}
      onBlur={input.onBlur}
      onFocus={input.onFocus}
      isInvalid={touched && Boolean(error)}
      errorMessage={touched && error ? error : null}
      {...props}
    />
  );
};
